import {
  BaseUserProfile, BookingProcessProgressBar,
  BookingState,
  EnabledFeatures,
  Gym, GymTypes, IDay, IEnabledFeatures, TimeSlotType,
} from 'fitbeat.models';
import { isGymMultiAreaGym } from '../../config';
import {
  UserProfile, UserWorkoutSession, WaitlistEntry, WorkoutSessionRequest,
} from '../../entities';
import { BookingProcessInfo } from '../../entities/bookings';

export enum BookingSteps {
  BookingView = 'BookingView',
  Dashboard = 'Dashboard',
  GymLocation = 'GymLocation',
  InviteFriends = 'InviteFriends',
  SelectStartTime = 'SelectStartTime',
  ClassDuration = 'ClassDuration',
  Done = 'Done',
  ConfirmChangeForPartnerBooking = 'ConfirmChangeForPartnerBooking',
  ChangeInvite = 'ChangeInvite',
  PartnerSearch = 'PartnerSearch',
  ConfirmCancelBooking = 'ConfirmCancelBooking',
  CustomiseWorkout = 'CustomiseWorkout',
  WorkoutSettingsOverview = 'WorkoutSettingsOverview',
  Nickname = 'Nickname',
  InvitesRecieved = 'InvitesRecieved',
  WaitlistView = 'WaitlistView',
  ConfirmCancelWaitlist = 'ConfirmCancelWaitlist',
  SkillLevel = 'SkillLevel',
  BookingComplete = 'BookingComplete',
  BookingChanged = 'BookingChanged',
  Metrics = 'Metrics',
  CheckIntoFirstBooking = 'CheckIntoFirstBooking',
  SelectEquipment = 'SelectEquipment',
  InviteHome = 'InviteHome',
  OpenInvitesReceived = 'OpenInvitesReceived',
  SetupMirror = 'SetupMirror',
  SetupHRMonitor = 'SetupHRMonitor',
  ConnectAppleHealthKit = 'ConnectAppleHealthKit',
  SetupDefaultHomeEquipment = 'SetupDefaultHomeEquipment',
  SelectStartDate = 'SelectStartDate',
  DemoStudioBookingSummary = 'DemoStudioBookingSummary',
  CheckIn = 'CheckIn',
  BagContact = 'BagContact',
  LiveStreamOptions = 'LiveStreamOptions',
  TypeOfWorkoutWarning = 'TypeOfWorkoutWarning',
  SelectOnDemandStudioSlot = 'SelectOnDemandStudioSlot',
  OnDemandStudioBookingCompleted = 'OnDemandStudioBookingCompleted',
}

const homeOnboardingStepsInOrder: BookingSteps[] = [
  BookingSteps.SetupDefaultHomeEquipment,
  BookingSteps.ConnectAppleHealthKit,
  BookingSteps.SetupHRMonitor,
  BookingSteps.SetupMirror,
];

export const canGoBackToTheseSteps: BookingSteps[] = [
  BookingSteps.GymLocation,
  BookingSteps.InviteFriends,
  BookingSteps.SelectStartTime,
  BookingSteps.SelectStartDate,
  BookingSteps.ClassDuration,
  BookingSteps.BookingView,
  BookingSteps.PartnerSearch,
  BookingSteps.ConfirmChangeForPartnerBooking,
  BookingSteps.SkillLevel,
  BookingSteps.LiveStreamOptions,
];

export enum ProcessTypes {
  Start = 'Start',
  Cancel = 'Cancel',
  ChangeBooking = 'ChangeBooking',
  BookingChanged = 'BookingChanged',
  ConfirmedPartnerBookingChange = 'ConfirmedPartnerBookingChange',
  ChangeInvite = 'ChangeInvite',
  ChangePartner = 'ChangePartner',
  ConfirmCancelBooking = 'ConfirmCancelBooking',
  CustomiseWorkout = 'CustomiseWorkout',
  TypeOfWorkoutWarning = 'TypeOfWorkoutWarning',
  WorkoutSettings = 'WorkoutSettings',
  BookingMade = 'BookingMade',
  PartnerChanged = 'PartnerChanged',
  BookingCancelled = 'BookingCancelled',
  WaitlistCancelled = 'WaitlistCancelled',
  CreatingBooking = 'CreatingBooking',
  ConfirmCancelWaitlist = 'ConfirmCancelWaitlist',
  CancelWaitlist = 'CancelWaitlist',
  FirstBookingComplete = 'FirstBookingComplete',
  BookingSettingsChanged = 'BookingSettingsChanged',
  InviteToHomeWorkout = 'InviteToHomeWorkout',
  StartInstant = 'StartInstant',
  DemoStudioBooking = 'DemoStudioBooking',
  CheckIn = 'CheckIn',
  BagContact = 'BagContact',
}

export interface IAdditionalOptions {
  isCapturingBasicInfo: boolean;
  allBookings: UserWorkoutSession[];
  enabledFeatures: IEnabledFeatures,
  workoutInviteId?: string;
  isSoloWorkout: boolean;
  selectedGym?: Gym;
  // Todo Remove isInviteFriendsEnabledForStudio. Use gym.partnerBookingsDisabled instead
  isInviteFriendsEnabledForStudio: boolean;
  onDemandStudioBooking: boolean;
  day: IDay | null;
  platformOS: string
  fromPlanner: boolean;
  isOfficeScheduleBooking: boolean;
  isFirstWorkout: boolean;
}

const startProcessSteps: BookingSteps[] = [
  BookingSteps.Dashboard,
  BookingSteps.SkillLevel,
];

export class BookingProcess {
  public static getNextStep(
    userProfile: UserProfile,
    userWorkoutRequest: WorkoutSessionRequest,
    processType: ProcessTypes,
    additionalOptions: IAdditionalOptions,
    waitlistEntry?: WaitlistEntry,
    currentBookingStep?: BookingSteps,
  )
    : BookingProcessInfo {
    const gym = additionalOptions?.selectedGym;
    const gymType = gym?.type;
    const isHomeGym = gymType === GymTypes.Home;
    BookingProcess.StudioTotalSteps = additionalOptions && additionalOptions.isInviteFriendsEnabledForStudio
      ? additionalOptions.isSoloWorkout
        ? this.minimumAmountOfBookingStepsStudio + this.extraStepsAmountForEnabledStudioParterInvite
        : this.minimumAmountOfBookingStepsStudio + this.extraStepsAmountForStudioParterInvite
      : this.minimumAmountOfBookingStepsStudio;
    BookingProcess.HomeTotalSteps = this.getMaxStepsForHomeBookingCreation(additionalOptions, gymType);

    if (processType === ProcessTypes.Start) {
      if (additionalOptions.isFirstWorkout && isHomeGym) {
        // this is checking for first workout, not first _home_ workout (this will be missed when a studio
        // workout was the first workout), but it aligns with the old isDemo check
        const nextOnboardingStep = BookingProcess.getHomeOnboardingStep(currentBookingStep, additionalOptions);
        if (nextOnboardingStep) {
          return nextOnboardingStep;
        }
      }
    }

    if (processType === ProcessTypes.DemoStudioBooking) {
      if (!currentBookingStep) {
        return BookingProcess.getBookingProcessInfo(BookingSteps.SelectStartDate);
      }
      if (currentBookingStep === BookingSteps.SelectStartDate) {
        return BookingProcess.getBookingProcessInfo(BookingSteps.SelectStartTime);
      }
      if (currentBookingStep === BookingSteps.SelectStartTime) {
        return BookingProcess.getBookingProcessInfo(BookingSteps.DemoStudioBookingSummary);
      }
    }

    if (processType === ProcessTypes.Start) {
      return this.processTypeStart(
        userProfile,
        userWorkoutRequest,
        additionalOptions,
        waitlistEntry,
        currentBookingStep,
        gym,
      );
    }

    if (processType === ProcessTypes.Cancel
      || processType === ProcessTypes.PartnerChanged
      || processType === ProcessTypes.BookingCancelled
      || processType === ProcessTypes.WaitlistCancelled) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.Dashboard);
    }

    if (processType === ProcessTypes.ChangeBooking) {
      if ((userWorkoutRequest && WorkoutSessionRequest.hasPairedWorkoutSession(userWorkoutRequest)
        || userWorkoutRequest && userWorkoutRequest.openInviteLink)
        && currentBookingStep === BookingSteps.BookingView) {
        return BookingProcess.getBookingProcessInfo(BookingSteps.ConfirmChangeForPartnerBooking);
      }

      const bookingProcessInfo = BookingProcess.getBookingCreationStepForType(
        userProfile,
        currentBookingStep,
        additionalOptions,
        gym,
        waitlistEntry,
      );

      if (bookingProcessInfo) {
        return bookingProcessInfo;
      }
    }

    if (processType === ProcessTypes.ConfirmedPartnerBookingChange) {
      const bookingProcessInfo = BookingProcess.getBookingCreationStepForType(
        userProfile,
        currentBookingStep,
        additionalOptions,
        gym,
      );
      if (bookingProcessInfo) {
        return bookingProcessInfo;
      }
    }

    if (processType === ProcessTypes.ChangeInvite) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.ChangeInvite);
    }

    if (processType === ProcessTypes.CheckIn) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.CheckIn);
    }

    if (processType === ProcessTypes.BagContact) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.BagContact);
    }

    if (processType === ProcessTypes.ChangePartner) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.PartnerSearch);
    }

    if (userWorkoutRequest && processType === ProcessTypes.ConfirmCancelBooking) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.ConfirmCancelBooking);
    }

    if (processType === ProcessTypes.CustomiseWorkout) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.CustomiseWorkout);
    }

    if (processType === ProcessTypes.TypeOfWorkoutWarning) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.TypeOfWorkoutWarning);
    }

    if (processType === ProcessTypes.InviteToHomeWorkout) {
      if (isHomeGym && this.canInviteToOpenBooking(additionalOptions)) {
        return BookingProcess.getBookingProcessInfo(
          BookingSteps.InviteHome,
          BookingProcess.HomeTotalSteps,
          BookingProcess.HomeTotalSteps,
        );
      }
      processType = ProcessTypes.BookingMade;
    }

    if (processType === ProcessTypes.WorkoutSettings) {
      if (!userProfile.skillLevel) {
        return BookingProcess.getBookingProcessInfo(BookingSteps.SkillLevel);
      }

      const amountOfStepsToSubtractFromHomeBooking = this.isHomeWithFriends(additionalOptions, gymType)
        ? this.extraStepsAmountForHomeFriendInvite : 0;
      const currentStep = gymType === GymTypes.Home
        ? BookingProcess.HomeTotalSteps - amountOfStepsToSubtractFromHomeBooking
        : BookingProcess.StudioTotalSteps;
      const totalSteps = gymType === GymTypes.Home
        ? BookingProcess.HomeTotalSteps
        : BookingProcess.StudioTotalSteps;
      return BookingProcess.getBookingProcessInfo(BookingSteps.WorkoutSettingsOverview, totalSteps, currentStep);
    }

    if (processType === ProcessTypes.ConfirmCancelWaitlist) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.ConfirmCancelWaitlist);
    }

    if (processType === ProcessTypes.FirstBookingComplete) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.BookingComplete);
    }

    if (processType === ProcessTypes.BookingSettingsChanged) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.BookingChanged);
    }

    if (processType === ProcessTypes.BookingMade) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.BookingComplete);
    }

    if (processType === ProcessTypes.BookingChanged) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.BookingComplete);
    }

    if (processType === ProcessTypes.StartInstant) {
      if (currentBookingStep === BookingSteps.ClassDuration) {
        return BookingProcess.getBookingProcessInfo(BookingSteps.BookingComplete);
      }

      return BookingProcess.getBookingProcessInfo(
        BookingSteps.ClassDuration,
        BookingProcess.HomeTotalSteps,
        this.getClassDurationCurrentProgressStep(gymType as any),
      );
    }

    return BookingProcess.getBookingProcessInfo(BookingSteps.Dashboard);
  }

  public static getPreviousStep(
    previousSteps: BookingProcessInfo[],
    currentStep: BookingSteps,
  ): BookingProcessInfo {
    const previousScreen = previousSteps.pop();
    const previousStep = previousScreen ? previousScreen.bookingStep : undefined;
    const allowedPreviousScreen = canGoBackToTheseSteps.find((step) => step === previousStep);
    return {
      bookingStep: allowedPreviousScreen || currentStep,
      progressBar: (allowedPreviousScreen && previousScreen) ? previousScreen.progressBar : undefined,
    };
  }

  public static isStepRequiredInfoCapture(bookingStep: BookingSteps): boolean {
    return (bookingStep === BookingSteps.Nickname
      || bookingStep === BookingSteps.Metrics);
  }

  public static getTimeSelectionProcessType(
    gymType: GymTypes,
    isInviteFriendsEnabledForStudio: boolean,
    isSoloWorkout: boolean,
    isInstantWorkout: boolean,
    workoutSession?: UserWorkoutSession,
  ): ProcessTypes {
    // THIS SEEMS LIKE A HACK
    if (isInstantWorkout) {
      return ProcessTypes.WorkoutSettings;
    }
    return workoutSession ? ProcessTypes.ChangeBooking : ProcessTypes.Start;
  }

  private static StudioTotalSteps: number;

  private static HomeTotalSteps: number;

  private static minimumAmountOfBookingStepsStudio = 4;

  private static extraStepsAmountForEnabledStudioParterInvite = 1;

  private static extraStepsAmountForStudioParterInvite = 2;

  private static minimumAmountOfBookingStepsForStudioAccounts = 6;

  private static minimumAmountOfBookingStepsHome = 4;

  private static extraStepsAmountForHomeFriendInvite = 1;

  private static getHomeOnboardingStep(
    currentBookingStep: BookingSteps | undefined,
    additionalOptions: IAdditionalOptions,
  ): BookingProcessInfo | undefined {
    let nextStepIndex;
    const { enabledFeatures } = additionalOptions;
    const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);
    const homeOnlyUserStartedBookingProcess = currentBookingStep === undefined && !hasAccessToAStudioGym;
    const userSelectedAHomeGymLocation = currentBookingStep === BookingSteps.GymLocation;
    const isFirstStepAfterHomeGymSelected = homeOnlyUserStartedBookingProcess || userSelectedAHomeGymLocation;
    const havePendingBookings = additionalOptions.allBookings?.length > 0;
    if (havePendingBookings) {
      return undefined;
    }

    const startOnboarding = currentBookingStep === undefined || isFirstStepAfterHomeGymSelected;
    if (startOnboarding) {
      nextStepIndex = 0;
    } else {
      const currentOnboardingIndex = homeOnboardingStepsInOrder.indexOf(currentBookingStep);
      const pastOnboarding = currentOnboardingIndex === -1;
      if (pastOnboarding) {
        return undefined;
      }
      nextStepIndex = currentOnboardingIndex + 1;
    }
    const nextOnboardingStep = homeOnboardingStepsInOrder[nextStepIndex];

    // skip past some steps based on additionalOptions
    const onIOS = additionalOptions.platformOS === 'ios';
    if (nextOnboardingStep === BookingSteps.ConnectAppleHealthKit && !onIOS) {
      return BookingProcess.getHomeOnboardingStep(nextOnboardingStep, additionalOptions);
    }

    if (nextOnboardingStep) {
      return BookingProcess.getBookingProcessInfo(nextOnboardingStep);
    }

    // done with onboarding
    return undefined;
  }

  private static processTypeStart(
    userProfile: UserProfile,
    userWorkoutRequest: WorkoutSessionRequest,
    additionalOptions: IAdditionalOptions,
    waitlistEntry?: WaitlistEntry,
    currentBookingStep?: BookingSteps,
    gym?: Gym,
  ): BookingProcessInfo {
    const { enabledFeatures } = additionalOptions;
    const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);
    if (!UserProfile.isUserPtApproved(userProfile)
      && !userWorkoutRequest
      && userProfile.timeSlotType === TimeSlotType.OffPeak
      && BookingProcess.hasAtleastOneBooking(additionalOptions)) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.CheckIntoFirstBooking);
    }

    if (!userProfile.nickname && hasAccessToAStudioGym) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.Nickname);
    }

    if (UserProfile.isUserPtApproved(userProfile) && !userProfile.weight) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.Metrics);
    }

    if (!userProfile.skillLevel) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.SkillLevel);
    }

    if (additionalOptions && additionalOptions.workoutInviteId) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.OpenInvitesReceived);
    }

    if (userWorkoutRequest && userWorkoutRequest.bookingState === BookingState.BookingInvite) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.InvitesRecieved);
    }

    if (userWorkoutRequest) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.BookingView);
    }

    if (waitlistEntry) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.WaitlistView);
    }
    return BookingProcess.getBookingCreationStepForType(
      userProfile,
      currentBookingStep,
      additionalOptions,
      gym,
      waitlistEntry,
    );
  }

  private static getBookingCreationStepForType(
    userProfile: UserProfile,
    currentBookingStep: BookingSteps = BookingSteps.Dashboard,
    additionalOptions: IAdditionalOptions,
    gym?: Gym,
    waitlistEntry?: WaitlistEntry,
  ): BookingProcessInfo {
    const gymType = gym?.type;
    const startProcessStep = startProcessSteps.find((step) => step === currentBookingStep);
    const { enabledFeatures } = additionalOptions;
    const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);
    if (startProcessStep) {
      const initialCurrentProgressStep = 1;
      return hasAccessToAStudioGym
        ? BookingProcess.getBookingProcessInfo(
          BookingSteps.GymLocation,
          BookingProcess.StudioTotalSteps,
          initialCurrentProgressStep,
        )
        : BookingProcess.getBookingProcessInfo(
          BookingSteps.ClassDuration,
          BookingProcess.HomeTotalSteps,
          initialCurrentProgressStep,
        );
    }

    if (gymType === GymTypes.Home) {
      if (additionalOptions.isOfficeScheduleBooking && gym?.officeGymConfig) {
        return this.getBookingCreationStepForOfficeSchedule(
          currentBookingStep,
          userProfile,
        );
      }
      return this.getBookingCreationStepForHome(
        currentBookingStep,
        userProfile,
        additionalOptions.isSoloWorkout,
      );
    }

    if (gymType === GymTypes.Indoor) {
      return this.getBookingCreationStepForStudio(
        currentBookingStep,
        additionalOptions,
        waitlistEntry,
        gym,
      );
    }
    return BookingProcess.getBookingProcessInfo(BookingSteps.Dashboard);
  }

  private static getBookingCreationStepForHome(
    currentBookingStep: BookingSteps = BookingSteps.Dashboard,
    userProfile: BaseUserProfile,
    isSoloWorkout: boolean,
  ): BookingProcessInfo {
    const userJustCompletedHomeOnboarding = homeOnboardingStepsInOrder.includes(currentBookingStep);
    if (currentBookingStep === BookingSteps.GymLocation
      || currentBookingStep === BookingSteps.BookingView
      || currentBookingStep === BookingSteps.ConfirmChangeForPartnerBooking
      || userJustCompletedHomeOnboarding) {
      const currentHomeInviteFriendsStep = 2;
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.ClassDuration,
        BookingProcess.HomeTotalSteps,
        currentHomeInviteFriendsStep,
      );
    }

    if (currentBookingStep === BookingSteps.ClassDuration) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.SelectStartTime,
        BookingProcess.HomeTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Home),
      );
    }

    if (currentBookingStep === BookingSteps.SelectStartTime) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.InviteFriends,
        BookingProcess.HomeTotalSteps,
        this.getClassDurationCurrentProgressStep(GymTypes.Home),
      );
    }

    if (currentBookingStep === BookingSteps.InviteFriends) {
      const shouldShowLiveStreamOptionsStep = BaseUserProfile.isInfluencer(userProfile) && !isSoloWorkout;
      if (shouldShowLiveStreamOptionsStep) {
        return BookingProcess.getBookingProcessInfo(
          BookingSteps.LiveStreamOptions,
          BookingProcess.HomeTotalSteps,
          this.getStartTimeCurrentProgressStep(GymTypes.Home),
        );
      }
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.WorkoutSettingsOverview,
        BookingProcess.HomeTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Home),
      );
    }

    if (currentBookingStep === BookingSteps.LiveStreamOptions) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.WorkoutSettingsOverview,
        BookingProcess.HomeTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Home),
      );
    }

    return BookingProcess.getBookingProcessInfo(BookingSteps.Dashboard);
  }

  private static getBookingCreationStepForOfficeSchedule(
    currentBookingStep: BookingSteps = BookingSteps.Dashboard,
    userProfile: BaseUserProfile,
  ): BookingProcessInfo {
    const totalSteps = 2;

    if (currentBookingStep === BookingSteps.GymLocation) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.ClassDuration,
        totalSteps,
        1,
      );
    }

    if (currentBookingStep === BookingSteps.ClassDuration) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.WorkoutSettingsOverview,
        totalSteps,
        2,
      );
    }

    return BookingProcess.getBookingProcessInfo(BookingSteps.Dashboard);
  }

  private static goToSelectTimeIfNotMultiAreaAndFromPlanner(additionalOptions: IAdditionalOptions, gym?: Gym) {
    if (gym && gym?.zoneMapping) {
      const isMultiAreaGym = isGymMultiAreaGym(gym);

      if (!isMultiAreaGym && additionalOptions.fromPlanner) {
        return BookingProcess.getBookingProcessInfo(
          BookingSteps.SelectStartTime,
          BookingProcess.StudioTotalSteps,
          this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
        );
      }
    }

    return BookingProcess.getBookingProcessInfo(
      BookingSteps.SelectStartDate,
      BookingProcess.StudioTotalSteps,
      this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
    );
  }

  private static getBookingCreationStepForStudio(
    currentBookingStep: BookingSteps = BookingSteps.Dashboard,
    additionalOptions: IAdditionalOptions,
    waitlistEntry?: WaitlistEntry,
    gym?: Gym,
  ): BookingProcessInfo {
    const shouldShowStartTIme = this.goToSelectTimeIfNotMultiAreaAndFromPlanner(additionalOptions, gym);

    if (currentBookingStep === BookingSteps.GymLocation
      || currentBookingStep === BookingSteps.BookingView
      || currentBookingStep === BookingSteps.ConfirmChangeForPartnerBooking) {
      if (gym?.singleStationPerZoneGym) {
        return shouldShowStartTIme;
      }

      const currentIndoorStartTimeProgressStep = 2;
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.InviteFriends,
        BookingProcess.StudioTotalSteps,
        currentIndoorStartTimeProgressStep,
      );
    }

    if (currentBookingStep === BookingSteps.InviteFriends) {
      return shouldShowStartTIme;
    }

    if (currentBookingStep === BookingSteps.SelectStartDate) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.SelectStartTime,
        BookingProcess.StudioTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
      );
    }

    if (currentBookingStep === BookingSteps.SelectStartTime && waitlistEntry) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.WaitlistView,
        BookingProcess.StudioTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
      );
    }

    if (currentBookingStep === BookingSteps.SelectStartTime && additionalOptions.onDemandStudioBooking) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.SelectOnDemandStudioSlot);
    }

    if (currentBookingStep === BookingSteps.SelectOnDemandStudioSlot) {
      return BookingProcess.getBookingProcessInfo(BookingSteps.WorkoutSettingsOverview);
    }

    const isPartnerBooking = !additionalOptions.isSoloWorkout && !gym?.singleStationPerZoneGym;
    if (currentBookingStep === BookingSteps.SelectStartTime && isPartnerBooking) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.PartnerSearch,
        BookingProcess.StudioTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
      );
    }

    if (currentBookingStep === BookingSteps.PartnerSearch) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.WorkoutSettingsOverview,
        BookingProcess.StudioTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
      );
    }

    if (currentBookingStep === BookingSteps.SelectStartTime && !isPartnerBooking) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.WorkoutSettingsOverview,
        BookingProcess.StudioTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
      );
    }

    if (currentBookingStep === BookingSteps.WaitlistView) {
      return BookingProcess.getBookingProcessInfo(
        BookingSteps.SelectStartTime,
        BookingProcess.StudioTotalSteps,
        this.getStartTimeCurrentProgressStep(GymTypes.Indoor),
      );
    }

    return BookingProcess.getBookingProcessInfo(BookingSteps.Dashboard);
  }

  private static hasAtleastOneBooking(additionalOptions?: IAdditionalOptions): boolean {
    return !!(additionalOptions && additionalOptions.allBookings && additionalOptions.allBookings.length > 0);
  }

  private static canInviteToOpenBooking(additionalOptions?: IAdditionalOptions): boolean {
    return !additionalOptions || additionalOptions.isSoloWorkout === false;
  }

  private static getStartTimeCurrentProgressStep(gymType: GymTypes) {
    const currentStartTimeStepForStudio = 3;
    const currentStartTimeStepForHome = 4;
    return gymType === GymTypes.Indoor
      ? currentStartTimeStepForStudio : currentStartTimeStepForHome;
  }

  private static getClassDurationCurrentProgressStep(gymType: GymTypes) {
    const currentClassDurationStepForStudio = 3;
    const currentClassDurationStepForHome = 3;
    return gymType === GymTypes.Indoor
      ? currentClassDurationStepForStudio : currentClassDurationStepForHome;
  }

  private static isHomeWithFriends(additionalOptions: IAdditionalOptions, gymType?: GymTypes): boolean {
    return (gymType === GymTypes.Home && this.canInviteToOpenBooking(additionalOptions));
  }

  private static getMaxStepsForHomeBookingCreation(
    additionalOptions: IAdditionalOptions,
    gymType?: GymTypes,
  ): number {
    const amountToAddForInvites = this.isHomeWithFriends(additionalOptions, gymType)
      ? this.extraStepsAmountForHomeFriendInvite : 0;
    const { enabledFeatures } = additionalOptions;
    const hasAccessToAStudioGym = EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);
    return hasAccessToAStudioGym
      ? this.minimumAmountOfBookingStepsForStudioAccounts + amountToAddForInvites
      : this.minimumAmountOfBookingStepsHome + amountToAddForInvites;
  }

  private static getBookingProcessInfo(
    bookingStep: BookingSteps,
    totalSteps?: number,
    currentStep?: number,
  ): BookingProcessInfo {
    let progressBar: BookingProcessProgressBar | undefined;
    if (totalSteps && currentStep) {
      progressBar = { totalSteps, currentStep };
    }
    return { bookingStep, progressBar };
  }
}
